import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle, ArrowRight, Wallet } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "checking", attempts: 0 });
  const [err, setErr] = useState("");
  const [balance, setBalance] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setErr("Missing session id in URL");
      setState({ phase: "error", attempts: 0 });
      return;
    }

    let n = 0;
    const poll = async () => {
      if (cancelled.current) return;
      n += 1;
      setState({ phase: "checking", attempts: n });
      try {
        // Try top-up first (most common path now)
        const { data } = await api.get(`/balance/topup/status/${sessionId}`);
        if (data.credited) {
          setBalance(data.balance_usd);
          setState({ phase: "credited", attempts: n });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", attempts: n });
          setErr("Payment session expired or failed.");
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempts: n });
          setErr("We could not confirm your payment in time. Check your email — if you were charged, your balance will update shortly.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        // 400 "Not a top-up session" → fall back to legacy /checkout/status
        if (e?.response?.status === 400 || e?.response?.status === 404) {
          try {
            const { data } = await api.get(`/checkout/status/${sessionId}`);
            if (data.payment_status === "paid" && data.order_id) {
              setState({ phase: "ordered", attempts: n });
              setTimeout(() => nav(`/orders/${data.order_id}`, { replace: true }), 600);
              return;
            }
          } catch {}
        }
        setErr(formatApiError(e));
        setState({ phase: "error", attempts: n });
      }
    };
    poll();

    return () => {
      cancelled.current = true;
    };
  }, [sessionId, nav]);

  return (
    <div data-testid="payment-success-page" className="container-pad py-20 md:py-28 max-w-2xl">
      <div className="card-flat p-10">
        {state.phase === "checking" && (
          <>
            <Loader2 size={28} className="animate-spin text-signal" />
            <div className="overline mt-6">// VERIFYING PAYMENT</div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
              Confirming your <span className="text-signal">top-up…</span>
            </h1>
            <p className="mt-3 text-zinc-400">
              Talking to our payment partner to confirm your payment. This usually takes a few seconds.
              Please don't close this tab.
            </p>
            <div className="mt-6 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
              Attempt {state.attempts} / {MAX_ATTEMPTS}
            </div>
          </>
        )}

        {state.phase === "credited" && (
          <>
            <CheckCircle2 size={28} className="text-signal" />
            <div className="overline mt-6">// PAYMENT CONFIRMED</div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
              Balance <span className="text-signal">credited.</span>
            </h1>
            <p className="mt-3 text-zinc-400">
              Your new balance is ready to spend. Activate a server or API from the catalog.
            </p>

            {balance != null && (
              <div className="mt-6 card-flat p-5 inline-flex items-center gap-3">
                <Wallet size={16} className="text-signal" />
                <div>
                  <div className="overline">CURRENT BALANCE</div>
                  <div className="mt-1 font-mono text-2xl text-white">${balance.toFixed(2)}</div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/catalog" className="btn-signal h-11 px-5 text-sm font-mono tracking-widest" data-testid="success-go-catalog">
                DEPLOY A SERVER <ArrowRight size={14} />
              </Link>
              <Link to="/dashboard" className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest">
                GO TO DASHBOARD
              </Link>
            </div>
          </>
        )}

        {state.phase === "ordered" && (
          <>
            <CheckCircle2 size={28} className="text-signal" />
            <div className="overline mt-6">// PAYMENT CONFIRMED</div>
            <h1 className="mt-3 font-display text-3xl tracking-tight">
              Provisioning your <span className="text-signal">server…</span>
            </h1>
            <p className="mt-3 text-zinc-400">Redirecting to your credentials.</p>
          </>
        )}

        {(state.phase === "failed" || state.phase === "error" || state.phase === "timeout") && (
          <>
            <XCircle size={28} className="text-red-400" />
            <div className="overline mt-6">
              {state.phase === "timeout" ? "// CONFIRMATION PENDING" : "// PAYMENT ISSUE"}
            </div>
            <h1 className="mt-3 font-display text-3xl tracking-tight">
              {state.phase === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-zinc-400">{err}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/dashboard" className="btn-signal h-11 px-5 text-sm font-mono tracking-widest">
                GO TO DASHBOARD <ArrowRight size={14} />
              </Link>
              <Link to="/topup" className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest">
                TRY AGAIN
              </Link>
            </div>
            <div className="mt-6 font-mono text-[11px] text-zinc-500">
              Need help? Email <a href="mailto:billing@cloudrentvps.com" className="text-signal hover:underline">billing@cloudrentvps.com</a> with session id <code className="text-zinc-300">{sessionId || "—"}</code>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
